import { useState, useEffect, useCallback } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Drawer, DrawerContent, DrawerNavigation } from "@progress/kendo-react-layout";
import { FaTimesCircle, FaPlusCircle } from "react-icons/fa";
import { getGoNoGos, GoNoGoItem } from "../../data/goNoGoData";
import AddGoNoGoForm from "../../components/FormComps/GoNoGo/AddGoNoGoForm";
import EditGoNoGoForm from "../../components/FormComps/GoNoGo/EditGoNoGoForm";
import "../../components/grids/Grids.css";
import "../Styles.css";
import "./GoNoGo.css";

const GoNoGo = (props: any) => {
    const [data, setData] = useState<GoNoGoItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [addFormVisible, setAddFormVisible] = useState(false);
    const [editFormVisible, setEditFormVisible] = useState(false);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const items = await getGoNoGos(showDeleted, showArchived);
            setData(items);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [showDeleted, showArchived]);

    useEffect(() => {
        load();
    }, [load, refreshKey]);

    const handleClose = () => {
        setExpanded(false);
        setAddFormVisible(false);
        setEditFormVisible(false);
        setSelectedId(null);
        setRefreshKey(k => k + 1);
    };

    const handleAddForm = () => {
        setSelectedId(null);
        setEditFormVisible(false);
        setAddFormVisible(true);
        setExpanded(true);
    };

    const handleRowClick = (e: any) => {
        setAddFormVisible(false);
        setSelectedId(e.dataItem.goNoGoId);
        setEditFormVisible(true);
        setExpanded(true);
    };

    const statusCell = (cellProps: any) => {
        const status = cellProps.dataItem.status || "New";
        const colorMap: Record<string, string> = {
            "New": "#3498db",
            "In Progress": "#f39c12",
            "Won": "#27ae60",
            "Lost": "#e74c3c",
        };
        const bg = colorMap[status] || "#95a5a6";
        return (
            <td>
                <span style={{
                    background: bg, color: "#fff", borderRadius: 4,
                    padding: "2px 8px", fontSize: "0.85em", fontWeight: 600
                }}>
                    {status}
                </span>
            </td>
        );
    };

    const goCell = (cellProps: any) => {
        const go = cellProps.dataItem.go;
        return (
            <td>
                {go ? (
                    <span style={{ background: "#27ae60", color: "#fff", borderRadius: 4, padding: "2px 8px", fontSize: "0.85em", fontWeight: 600 }}>Yes</span>
                ) : (
                    <span style={{ background: "#e74c3c", color: "#fff", borderRadius: 4, padding: "2px 8px", fontSize: "0.85em", fontWeight: 600 }}>No</span>
                )}
            </td>
        );
    };

    const valueCell = (cellProps: any) => (
        <td>{cellProps.dataItem.value != null ? `£${Number(cellProps.dataItem.value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : ""}</td>
    );

    const dateCell = (cellProps: any) => (
        <td>{cellProps.dataItem.dueDate ? new Date(cellProps.dataItem.dueDate).toLocaleDateString('en-GB') : ""}</td>
    );

    return (
        <div>
            <Drawer
                expanded={expanded}
                position="end"
                width={750}
                mode="overlay"
                animation={{ duration: 400 }}
                onOverlayClick={handleClose}
            >
                <DrawerContent>
                    <DrawerNavigation>
                        {addFormVisible && (
                            <>
                                <div className="form__title__container">
                                    <h1 className="form__title">Add Go/No-Go</h1>
                                    <FaTimesCircle className="closeform" onClick={handleClose} />
                                </div>
                                <div className="form__wrapper">
                                    <AddGoNoGoForm onClose={handleClose} lockEdit={props.lockEdit} />
                                </div>
                            </>
                        )}
                        {editFormVisible && selectedId != null && (
                            <>
                                <div className="form__title__container">
                                    <h1 className="form__title">Edit Go/No-Go</h1>
                                    <FaTimesCircle className="closeform" onClick={handleClose} />
                                </div>
                                <EditGoNoGoForm
                                    goNoGoId={selectedId}
                                    onClose={handleClose}
                                    lockEdit={props.lockEdit}
                                />
                            </>
                        )}
                    </DrawerNavigation>

                    <div className="p-2">
                        <div className="grid__title__container" style={{ marginLeft: '-1rem', marginRight: '-1rem' }}>
                            <div className="grid__header__background">
                                <div className="grid__title">
                                    <div>Go/No-Go</div>
                                    <div style={{ display: "flex", flexDirection: "row" }}>
                                        {!props.lockEdit && (
                                            <div onClick={handleAddForm} className="grid__addrecord__button">
                                                <FaPlusCircle style={{ fontSize: "14pt", marginRight: "1rem" }} />
                                                <p style={{ margin: 0 }}>Add new Go/No-Go</p>
                                            </div>
                                        )}
                                        <div
                                            onClick={() => setShowArchived(a => !a)}
                                            className="grid__addrecord__button"
                                            style={{ marginLeft: 8 }}
                                        >
                                            <p style={{ margin: 0 }}>{showArchived ? "Hide Archived" : "Show Archived"}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="h-2" />

                        <Grid
                            data={data}
                            onRowClick={handleRowClick}
                            style={{ cursor: "pointer" }}
                        >
                            <GridColumn field="reference" title="Reference" width="130px" />
                            <GridColumn field="client" title="Client" width="200px" />
                            <GridColumn field="sector" title="Sector" width="140px" />
                            <GridColumn field="value" title="Value" width="120px" cell={valueCell} />
                            <GridColumn field="go" title="Go" width="80px" cell={goCell} />
                            <GridColumn field="dueDate" title="Due Date" width="120px" cell={dateCell} />
                            <GridColumn field="location" title="Location" width="200px" />
                        </Grid>
                    </div>
                </DrawerContent>
            </Drawer>
        </div>
    );
};

export default GoNoGo;

